import React, { Dispatch, SetStateAction, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown, Form } from 'react-bootstrap';
import { format, startOfMonth, startOfWeek, startOfYear, subDays } from 'date-fns';

import { UseOutletContextPurchase } from '../Purchase';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { ProviderOrder } from '../../../../models/provider.order.model';
import { ProviderInvoice } from '../../../../models/provider.invoice.model';
import { Expense } from '../../../../models/expense.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { ProviderOrderGroup } from '../../../../models/provider.order.group.model';
import { ProviderInvoiceGroup } from '../../../../models/provider.invoice.group.model';
import { ExpenseGroup } from '../../../../models/expense.group.model';
import { paymentAllValue, paymentStatusList } from '../../../../libraries/payment.library';
import { statusDocumentValue, statusExpense, statusProviderInvoiceList, statusProviderOrderList } from '../../../../libraries/status.document.library';
import { validityList, validityValue } from '../../../../libraries/validation.library';
import { dateFormat } from '../../../../scripts/date.time.format.script';
import { contactSelected, treasuryAccountSelected } from '../../../../scripts/list.selected.script';
import { companyYearList } from '../../../../scripts/list.filter.script';
import { ApiDigitalAdminContact } from '../../../../services/admin/contact.service';
import { normalizeContacts } from '../../../../scripts/format.value.script';

import AppStorageRemove from '../../../../components/element/StorageRemove';
import AppModalProviderInvoiceExport from '../../../../components/modal/ModalProviderInvoiceExport';
import AppModalProviderOrderExport from '../../../../components/modal/ModalProviderOrderExport';
import AppModalExpenseExport from '../../../../components/modal/ModalExpenseExport';
import { modalShow } from '../../../../utils/modal.util';

export interface AppPurchaseListHeaderProps {
  purchase: string,
  storage: any,
  button: {filter: boolean, group: boolean},
  filter: {interval: string, start_date: string, end_date: string, contact: string, contact_label: string, treasury: string, validity: string, status: string, payment: string, sort_field: string, sort_mode: string, group: string, search: string},
  records: any[] | ProviderOrder[] | ProviderInvoice[] | Expense[],
  recordGroups: any[] | ProviderOrderGroup[] | ProviderInvoiceGroup[] | ExpenseGroup[],
  setFilter: Dispatch<SetStateAction<{interval: string, start_date: string, end_date: string, contact: string, contact_label: string, treasury: string, validity: string, status: string, payment: string, sort_field: string, sort_mode: string, group: string, search: string}>>,
  loadDocuments: Function,
  totalItems?: number
};

const AppPurchaseListHeader: React.FunctionComponent<AppPurchaseListHeaderProps> = ({purchase, storage, button, filter, records, recordGroups, setFilter, loadDocuments, totalItems}) => {
  const {companyForUser, settingsForCompany, treasuryAccountsForCompany, contactsForCompany} = UseOutletContextPurchase()
  const vatSubject = (settingsForCompany?.subject_tax ?? companyForUser?.subject_tax) === true
  const {lang} = StateLanguage()
  const navigate = useNavigate()

  const handleChangeInterval = (event: React.ChangeEvent <HTMLFormElement | HTMLSelectElement>) => {
    let startDate: Date = new Date()
    let endDate: Date = new Date()

    switch (event.target.value) {
      case 'today':
        setFilter({...filter, interval: event.target.value, start_date: format(startDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format(endDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
      case 'yesterday':
        setFilter({...filter, interval: event.target.value, start_date: format(subDays(startDate, 1), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format(subDays(endDate, 1), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
      case 'week':
        setFilter({...filter, interval: event.target.value, start_date: format(startOfWeek(startDate), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format(endDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
      case 'month':
        setFilter({...filter, interval: event.target.value, start_date: format(startOfMonth(startDate), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format(endDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
      case 'year':
        setFilter({...filter, interval: event.target.value, start_date: format(startOfYear(startDate), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format(endDate, `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
      default:
        setFilter({...filter, interval: event.target.value, start_date: format((new Date(`${event.target.value}-01-01T00:00:00.000`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`), end_date: format((new Date(`${event.target.value}-12-31T23:59:59.999`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`)})
        break;
    }
  }

  const handleChangeStartDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let date: string = (event.target.value.length > 0) ? format((new Date(`${event.target.value}T00:00:00.000`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`) : ''
    setFilter({...filter, interval: '', start_date: date})
  }

  const handleChangeEndDate = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    let date: string = (event.target.value.length > 0) ? format((new Date(`${event.target.value}T00:00:00.000`)), `yyyy-MM-dd'T'HH:mm:ss.SSSXXX`) : ''
    setFilter({...filter, interval: '', end_date: date})
  }

  const [contactSearch, setContactSearch] = useState<string>('')
  const [contactDropdownOpen, setContactDropdownOpen] = useState<boolean>(false)
  const [contactResults, setContactResults] = useState<any[]>([])
  const contactAutocompleteRef = useRef<HTMLDivElement>(null)
  const contactSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const contactSearchAbortRef = useRef<AbortController | null>(null)

  const getContactDisplayName = (item: any): string => {
    return item.type === "person" ? `${item.data.first_name ?? ''} ${item.data.last_name ?? ''}`.trim() : `${item.data.name ?? ''}`
  }

  const handleSelectContact = (contactId: string) => {
    const selected = contactResults.find((c: any) => c.id === contactId)
      ?? (contactsForCompany ?? []).find((c: any) => c.id === contactId)
    if (selected) {
      const label = getContactDisplayName(selected)
      setContactSearch(label)
      setFilter({...filter, contact: contactId, contact_label: label})
    }
    setContactDropdownOpen(false)
  }

  const handleClearContact = () => {
    setContactSearch('')
    setContactResults([])
    setFilter({...filter, contact: '', contact_label: ''})
    setContactDropdownOpen(false)
    if (contactSearchTimerRef.current) clearTimeout(contactSearchTimerRef.current)
    if (contactSearchAbortRef.current) contactSearchAbortRef.current.abort()
  }

  const handleContactInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!companyForUser) return
    const value = e.target.value
    setContactSearch(value)

    setFilter({...filter, contact: ''})

    if (value.length < 2) {
      setContactDropdownOpen(false)
      setContactResults([])
      if (contactSearchTimerRef.current) clearTimeout(contactSearchTimerRef.current)
      if (contactSearchAbortRef.current) contactSearchAbortRef.current.abort()
      return
    }

    setContactDropdownOpen(true)

    if (contactSearchTimerRef.current) clearTimeout(contactSearchTimerRef.current)
    if (contactSearchAbortRef.current) contactSearchAbortRef.current.abort()

    contactSearchTimerRef.current = setTimeout(() => {
      const abort = new AbortController()
      contactSearchAbortRef.current = abort
      ApiDigitalAdminContact.getContacts(companyForUser.id, value, '', '', '', 1, 20, abort.signal)
        .then((response: any) => {
          if (response.status === 0) return
          if (response.status === 200) {
            setContactResults(normalizeContacts(response.data.items))
          }
        })
    }, 300)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (contactAutocompleteRef.current && !contactAutocompleteRef.current.contains(event.target as Node)) {
        setContactDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (filter.contact === '') {
      setContactSearch('')
    } else if (filter.contact_label) {
      setContactSearch(filter.contact_label)
    } else {
      const found = (contactsForCompany ?? []).find((c: any) => c.id === filter.contact)
      if (found) {
        setContactSearch(getContactDisplayName(found))
      } else if (companyForUser) {
        ApiDigitalAdminContact.getContact(companyForUser.id, filter.contact).then((response: any) => {
          if (response.status === 200 && response.data) {
            setContactSearch(getContactDisplayName(response.data))
          }
        })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.contact, contactsForCompany])

  const handleChangeTreasury = (event: React.ChangeEvent <HTMLFormElement | HTMLSelectElement>) => {
    setFilter({...filter, treasury: event.target.value})
  }

  const handleChangeValidity = (event: React.ChangeEvent <HTMLFormElement | HTMLSelectElement>) => {
    setFilter({...filter, validity: event.target.value})
  }

  const handleChangeStatus = (event: React.ChangeEvent <HTMLFormElement | HTMLSelectElement>) => {
    setFilter({...filter, status: event.target.value})
  }

  const handleChangePayment = (event: React.ChangeEvent <HTMLFormElement | HTMLSelectElement>) => {
    setFilter({...filter, payment: event.target.value})
  }

  const executeGroupList = (item: string) => {
    if (companyForUser) {
      setFilter({...filter, group: item, interval: '', start_date: '', end_date: '', contact: '', contact_label: '', treasury: '', validity: '', status: '', payment: '', sort_field: '', sort_mode: ''})
      loadDocuments(companyForUser.id)
    }
  }

  const executeFilterRemove = (item: string) => {
    if (companyForUser && (filter.interval.length !== 0 || filter.start_date.length !== 0 || filter.end_date.length !== 0 || filter.contact.length !== 0 || filter.treasury.length !== 0 || filter.validity.length !== 0 || filter.status.length !== 0 || filter.payment.length !== 0)) {
      const newFilter = {...filter, [item]: ''}
      if (item === "start_date" || item === "end_date") newFilter.interval = ''
      if (item === "interval") { newFilter.start_date = ''; newFilter.end_date = '' }
      if (item === "contact") newFilter.contact_label = ''
      setFilter(newFilter)
      loadDocuments(companyForUser.id)
    }
  }

  const executeFilterReset = () => {
    if (companyForUser && (filter.interval.length !== 0 || filter.start_date.length !== 0 || filter.end_date.length !== 0 || filter.contact.length !== 0 || filter.treasury.length !== 0 || filter.validity.length !== 0 || filter.status.length !== 0 || filter.payment.length !== 0)) {
      setFilter({...filter, interval: '', start_date: '', end_date: '', contact: '', contact_label: '', treasury: '', validity: '', status: '', payment: '', sort_field: '', sort_mode: ''})
      loadDocuments(companyForUser.id)
    }
  }

  const executeFilterApply = () => {
    if (companyForUser && (filter.interval.length !== 0 || filter.start_date.length !== 0 || filter.end_date.length !== 0 || filter.contact.length !== 0 || filter.treasury.length !== 0 || filter.validity.length !== 0 || filter.status.length !== 0 || filter.payment.length !== 0)) {
      setFilter({...filter, sort_field: '', sort_mode: '', group: ''})
      loadDocuments(companyForUser.id)
    }
  }

  const [searchText, setSearchText] = useState(filter.search)
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current)
      if (contactSearchTimerRef.current) clearTimeout(contactSearchTimerRef.current)
      if (contactSearchAbortRef.current) contactSearchAbortRef.current.abort()
    }
  }, [])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setSearchText(value)

    if (searchTimerRef.current) clearTimeout(searchTimerRef.current)

    searchTimerRef.current = setTimeout(() => {
      setFilter({...filter, search: value})
      if (companyForUser) loadDocuments(companyForUser.id)
    }, 400)
  }

  const executeNavigateCreate = () => {
    if ((treasuryAccountsForCompany ?? []).length > 0) {
      navigate(`/main/purchase/${purchase}/create`)
    } else {
      GlobalSwal.fire({
        title: lang.labels.thereAreNoTreasuryAccounts,
        text: lang.labels.toCreateAnDocument,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesCreateTreasuryAccount,
        cancelButtonText: lang.labels.noCancel
      }).then( (result) => {
        if (result.isConfirmed) {
          navigate('/main/treasury/create')
        }
      })
    }
  }

  const executeShowModalExport = () => {
    const modalIds: Record<string, string> = {
      invoice: 'modal-provider-invoice-export',
      order: 'modal-provider-order-export',
      expense: 'modal-expense-export'
    }
    const modalId = modalIds[purchase]
    if (modalId) modalShow(modalId)
  }

  function status(): {code: string, display: string}[] {
    switch (purchase) {
      case 'order':
        return statusProviderOrderList(lang)
      case 'invoice':
        return statusProviderInvoiceList(lang)
      case 'expense':
        return statusExpense(lang)
      default:
        return []
    }
  }

  return (
    <>
      { companyForUser && records && recordGroups &&
        <>
          <div className="card-header">
            <div className="card-title">
              <div className="badge badge-lg badge-light-primary">
                <div className="d-flex align-items-center flex-wrap">
                  <span className="svg-icon svg-icon-2 svg-icon-primary me-3">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path opacity="0.3" d="M14.1 15.013C14.6 16.313 14.5 17.813 13.7 19.113C12.3 21.513 9.29999 22.313 6.89999 20.913C5.29999 20.013 4.39999 18.313 4.39999 16.613C5.09999 17.013 5.99999 17.313 6.89999 17.313C8.39999 17.313 9.69998 16.613 10.7 15.613C11.1 15.713 11.5 15.813 11.9 15.813C12.7 15.813 13.5 15.513 14.1 15.013ZM8.5 12.913C8.5 12.713 8.39999 12.513 8.39999 12.313C8.39999 11.213 8.89998 10.213 9.69998 9.613C9.19998 8.313 9.30001 6.813 10.1 5.513C10.6 4.713 11.2 4.11299 11.9 3.71299C10.4 2.81299 8.49999 2.71299 6.89999 3.71299C4.49999 5.11299 3.70001 8.113 5.10001 10.513C5.80001 11.813 7.1 12.613 8.5 12.913ZM16.9 7.313C15.4 7.313 14.1 8.013 13.1 9.013C14.3 9.413 15.1 10.513 15.3 11.713C16.7 12.013 17.9 12.813 18.7 14.113C19.2 14.913 19.3 15.713 19.3 16.613C20.8 15.713 21.8 14.113 21.8 12.313C21.9 9.513 19.7 7.313 16.9 7.313Z" fill="black" />
                      <path d="M9.69998 9.61307C9.19998 8.31307 9.30001 6.81306 10.1 5.51306C11.5 3.11306 14.5 2.31306 16.9 3.71306C18.5 4.61306 19.4 6.31306 19.4 8.01306C18.7 7.61306 17.8 7.31306 16.9 7.31306C15.4 7.31306 14.1 8.01306 13.1 9.01306C12.7 8.91306 12.3 8.81306 11.9 8.81306C11.1 8.81306 10.3 9.11307 9.69998 9.61307ZM8.5 12.9131C7.1 12.6131 5.90001 11.8131 5.10001 10.5131C4.60001 9.71306 4.5 8.91306 4.5 8.01306C3 8.91306 2 10.5131 2 12.3131C2 15.1131 4.2 17.3131 7 17.3131C8.5 17.3131 9.79999 16.6131 10.8 15.6131C9.49999 15.1131 8.7 14.1131 8.5 12.9131ZM18.7 14.1131C17.9 12.8131 16.7 12.0131 15.3 11.7131C15.3 11.9131 15.4 12.1131 15.4 12.3131C15.4 13.4131 14.9 14.4131 14.1 15.0131C14.6 16.3131 14.5 17.8131 13.7 19.1131C13.2 19.9131 12.6 20.5131 11.9 20.9131C13.4 21.8131 15.3 21.9131 16.9 20.9131C19.3 19.6131 20.1 16.5131 18.7 14.1131Z" fill="black" />
                    </svg>
                  </span>
                  <span>{lang.labels[`${purchase === "expense" ? purchase : `provider${purchase.charAt(0).toUpperCase()}${purchase.slice(1)}`}s` as keyof typeof lang.labels]}</span>
                  <span className="svg-icon svg-icon-2 svg-icon-primary mx-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12.6343 12.5657L8.45001 16.75C8.0358 17.1642 8.0358 17.8358 8.45001 18.25C8.86423 18.6642 9.5358 18.6642 9.95001 18.25L15.4929 12.7071C15.8834 12.3166 15.8834 11.6834 15.4929 11.2929L9.95001 5.75C9.5358 5.33579 8.86423 5.33579 8.45001 5.75C8.0358 6.16421 8.0358 6.83579 8.45001 7.25L12.6343 11.4343C12.9467 11.7467 12.9467 12.2533 12.6343 12.5657Z" fill="black" />
                    </svg>
                  </span>
                  <span className="text-capitalize">{button.group ? lang.labels.groups : lang.labels.listing}</span>
                  <span className="svg-icon svg-icon-2 svg-icon-primary mx-1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12.6343 12.5657L8.45001 16.75C8.0358 17.1642 8.0358 17.8358 8.45001 18.25C8.86423 18.6642 9.5358 18.6642 9.95001 18.25L15.4929 12.7071C15.8834 12.3166 15.8834 11.6834 15.4929 11.2929L9.95001 5.75C9.5358 5.33579 8.86423 5.33579 8.45001 5.75C8.0358 6.16421 8.0358 6.83579 8.45001 7.25L12.6343 11.4343C12.9467 11.7467 12.9467 12.2533 12.6343 12.5657Z" fill="black" />
                    </svg>
                  </span>
                  <span>{button.group ? `${recordGroups.length} ${lang.labels.collections}` : `${totalItems ?? records.length} ${lang.labels.items}`}</span>
                </div>
              </div>
            </div>
            <div className="card-toolbar">
              <div className="d-flex align-items-center position-relative mb-1 me-2">
                <span className="svg-icon svg-icon-1 position-absolute ms-4">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="black" />
                    <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="black" />
                  </svg>
                </span>
                <input
                  type="text"
                  className="form-control w-250px ps-14"
                  placeholder={lang.labels.search}
                  value={searchText}
                  onChange={handleSearchChange}
                />
              </div>
              <Dropdown>
                <Dropdown.Toggle variant={`btn ${button.filter ? "btn-primary" : "btn-light-primary"} mb-1 me-2`}>
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-2 ms-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path d="M19.0759 3H4.72777C3.95892 3 3.47768 3.83148 3.86067 4.49814L8.56967 12.6949C9.17923 13.7559 9.5 14.9582 9.5 16.1819V19.5072C9.5 20.2189 10.2223 20.7028 10.8805 20.432L13.8805 19.1977C14.2553 19.0435 14.5 18.6783 14.5 18.273V13.8372C14.5 12.8089 14.8171 11.8056 15.408 10.964L19.8943 4.57465C20.3596 3.912 19.8856 3 19.0759 3Z" fill="black" />
                      </svg>
                    </span>
                    {lang.labels.filter}
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown w-300px w-md-325px" align="start">
                  <div className="mt-5 mx-5">
                    <div className="fs-5 text-dark fw-bolder">{lang.labels.filterOptions}</div>
                  </div>
                  <div className="separator border-gray-200"></div>
                  <div className="mt-3 m-5">
                    <div className="row mb-2">
                      <label className="col-5 col-form-label-sm fw-bold fs-7">{lang.labels.timeInterval}:</label>
                      <div className="col-7 align-content-center">
                        <Form.Select bsPrefix="form-select form-select-sm" aria-placeholder={lang.labels.select} name="interval" value={filter.interval} onChange={handleChangeInterval}>
                          <option value="" className="d-none">{lang.labels.select}</option>
                          <option value="today">{lang.labels.today}</option>
                          <option value="yesterday">{lang.labels.yesterday}</option>
                          <option value="week">{lang.labels.thisWeek}</option>
                          <option value="month">{lang.labels.thisMonth}</option>
                          <option value="year">{lang.labels.thisYear}</option>
                          { companyForUser?.created_at && companyYearList(companyForUser.created_at).filter((item) => (item !== new Date().getFullYear())).map (( (item, index) => { return (
                            <option key={index} value={item}>{lang.labels.year} {item}</option>
                          )}))}
                        </Form.Select>
                      </div>
                    </div>
                    <div className="row mb-2">
                      <div className="col-md-6 mb-2 mb-md-0">
                        <label className="form-label fs-7">{lang.labels.startDate}:</label>
                        <input className="form-control form-control-sm" type="date" name="start-date" max={filter.end_date?.substring(0, 10) ?? ''} value={filter.start_date?.substring(0, 10) ?? ''} onChange={handleChangeStartDate} />
                      </div>
                      <div className="col-md-6">
                        <label className="form-label fs-7">{lang.labels.endDate}:</label>
                        <input className="form-control form-control-sm" type="date" name="end-date" min={filter.start_date?.substring(0, 10) ?? ''} value={filter.end_date?.substring(0, 10) ?? ''} onChange={handleChangeEndDate} />
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="form-label fs-7">{lang.labels.provider}:</label>
                      <div ref={contactAutocompleteRef} style={{position: 'relative'}}>
                        <div className="d-flex align-items-center">
                          <input
                            className="form-control form-control-sm"
                            type="text"
                            placeholder={lang.labels.searchContact ?? 'Rechercher un contact...'}
                            value={contactSearch}
                            onChange={handleContactInputChange}
                            onFocus={() => { if (contactSearch.length >= 2) setContactDropdownOpen(true) }}
                          />
                          { filter.contact.length > 0 &&
                            <button type="button" className="btn btn-sm btn-icon btn-light-danger ms-1 p-0" style={{minWidth: '24px', height: '24px'}} onClick={handleClearContact}>
                              <span style={{fontSize: '12px', lineHeight: 1}}>&times;</span>
                            </button>
                          }
                        </div>
                        { contactDropdownOpen && contactResults.length > 0 &&
                          <div className="bg-white border rounded shadow-sm" style={{position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1050, maxHeight: '200px', overflowY: 'auto'}}>
                            { contactResults.map((item: any, index: number) => (
                              <div key={index} className="px-3 py-2 cursor-pointer text-hover-primary" style={{cursor: 'pointer', fontSize: '13px'}} onClick={() => handleSelectContact(item.id)}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f8fa')}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}>
                                {getContactDisplayName(item)}
                              </div>
                            ))}
                          </div>
                        }
                      </div>
                    </div>
                    <div className="mb-2">
                      <label className="form-label fs-7">{lang.labels.treasury}:</label>
                      <Form.Select bsPrefix="form-select form-select-sm" aria-placeholder={lang.labels.select} name="treasury" value={filter.treasury} onChange={handleChangeTreasury}>
                        <option value="" className={`${(treasuryAccountsForCompany ?? []).length > 0 && "d-none"}`}>{lang.labels.select}</option>
                        { (treasuryAccountsForCompany ?? []).map (( (item, index) => { return (
                          <option key={index} value={item.id}>{item.name}</option>
                        )}))}
                      </Form.Select>
                    </div>
                    { purchase !== "order" &&
                      <div className="mb-2">
                        <label className="form-label fs-7">{lang.labels.validity}:</label>
                        <Form.Select bsPrefix="form-select form-select-sm" aria-placeholder={lang.labels.select} name="validity" value={filter.validity} onChange={handleChangeValidity}>
                          <option value="" className="d-none">{lang.labels.select}</option>
                          { validityList(lang).map (( (item, index) => { return (
                            <option key={index} value={item.code}>{item.display}</option>
                          )}))}
                        </Form.Select>
                      </div>
                    }
                    <div className="mb-2">
                      <label className="form-label fs-7">{lang.labels.status}:</label>
                      <Form.Select bsPrefix="form-select form-select-sm" aria-placeholder={lang.labels.select} name="status" value={filter.status} onChange={handleChangeStatus}>
                        <option value="" className="d-none">{lang.labels.select}</option>
                        { status().map (( (item, index) => { return (
                          <option key={index} value={item.code}>{item.display}</option>
                        )}))}
                      </Form.Select>
                    </div>
                    { purchase === "invoice" &&
                      <div className="mb-2">
                        <label className="form-label fs-7">{lang.labels.payment}:</label>
                        <Form.Select bsPrefix="form-select form-select-sm" aria-placeholder={lang.labels.select} name="payment-status" value={filter.payment} onChange={handleChangePayment}>
                          <option value="" className="d-none">{lang.labels.select}</option>
                          { paymentStatusList(lang).map (( (item, index) => { return (
                            <option key={index} value={item.code}>{item.display}</option>
                          )}))}
                        </Form.Select>
                      </div>
                    }
                    <div className="d-flex justify-content-end mt-5">
                      <Dropdown.Item bsPrefix="btn btn-sm btn-light btn-active-light-primary" onClick={executeFilterReset}>{lang.labels.reset}</Dropdown.Item>
                      <Dropdown.Item bsPrefix="btn btn-sm btn-primary ms-3" onClick={executeFilterApply}>{lang.labels.apply}</Dropdown.Item>
                    </div>
                  </div>
                </Dropdown.Menu>
              </Dropdown>
              <Dropdown>
                <Dropdown.Toggle variant={`btn ${button.group ? "btn-primary" : "btn-light-primary"} mb-1 me-2`}>
                  <div className="d-flex align-items-center">
                    <span className="svg-icon svg-icon-2 ms-0">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 21H8C7.4 21 7 20.6 7 20V4C7 3.4 7.4 3 8 3H16C16.6 3 17 3.4 17 4V20C17 20.6 16.6 21 16 21Z" fill="black"/>
                        <path opacity="0.3" d="M2 3H4C4.6 3 5 3.4 5 4V20C5 20.6 4.6 21 4 21H2V3ZM20 21H22V3H20C19.4 3 19 3.4 19 4V20C19 20.6 19.4 21 20 21Z" fill="black"/>
                      </svg>
                    </span>
                    {lang.labels.groupBy}
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown w-150px w-lg-175px" align="end">
                  <Dropdown.Divider />
                  <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "provider"} onClick={() => executeGroupList("provider")}>{lang.labels.provider}</Dropdown.Item>
                  <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "treasury"} onClick={() => executeGroupList("treasury")}>{lang.labels.treasury}</Dropdown.Item>
                  { purchase !== "order" &&
                    <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "validity"} onClick={() => executeGroupList("validity")}>{lang.labels.validity}</Dropdown.Item>
                  }
                  <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "status"} onClick={() => executeGroupList("status")}>{lang.labels.status}</Dropdown.Item>
                  { purchase === "invoice" &&
                    <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "payment"} onClick={() => executeGroupList("payment")}>{lang.labels.payment}</Dropdown.Item>
                  }
                  <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "year"} onClick={() => executeGroupList("year")}>{lang.labels.year}</Dropdown.Item>
                  <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "month"} onClick={() => executeGroupList("month")}>{lang.labels.month}</Dropdown.Item>
                  <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "week"} onClick={() => executeGroupList("week")}>{lang.labels.week}</Dropdown.Item>
                  <Dropdown.Item bsPrefix="dropdown-item fw-bold m-0 py-1" active={filter.group === "day"} onClick={() => executeGroupList("day")}>{lang.labels.day}</Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item bsPrefix="dropdown-item fw-bolder m-0 py-1" onClick={() => executeGroupList("")}>{lang.labels.doNotGroup}</Dropdown.Item>
                  <Dropdown.Divider />
                </Dropdown.Menu>
              </Dropdown>
              <button className="btn btn-primary mb-1 me-2" type="button" onClick={executeShowModalExport}>
                <div className="d-flex align-items-center">
                  <span className="svg-icon svg-icon-2 ms-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path opacity="0.3" d="M19 15C19 17.8 16.8 20 14 20H8V4H14C16.8 4 19 6.2 19 9C19 10.2 18.5 11.3 17.7 12C18.5 12.7 19 13.8 19 15Z" fill="black"/>
                      <path d="M12 2H6C5.4 2 5 2.4 5 3V21C5 21.6 5.4 22 6 22H12C16.4 22 20 18.4 20 14V10C20 5.6 16.4 2 12 2ZM12 20H7V4H12C15.3 4 18 6.7 18 10V14C18 17.3 15.3 20 12 20Z" fill="black"/>
                    </svg>
                  </span>
                  {lang.labels.export}
                </div>
              </button>
              <button className="btn btn-primary mb-1 me-0" type="button" onClick={executeNavigateCreate}>
                <div className="d-flex align-items-center">
                  <span className="svg-icon svg-icon-2 ms-0">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <rect opacity="0.3" x="2" y="2" width="20" height="20" rx="5" fill="black" />
                      <rect x="10.8891" y="17.8033" width="12" height="2" rx="1" transform="rotate(-90 10.8891 17.8033)" fill="black" />
                      <rect x="6.01041" y="10.9247" width="12" height="2" rx="1" fill="black" />
                    </svg>
                  </span>
                  {lang.labels.addDocument}
                </div>
              </button>
            </div>
          </div>
          { storage && button.filter &&
            <div className="card-scroll d-flex bg-light-primary rounded border-0 p-3 mx-7 mb-4">
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.interval.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("interval")}>
                <AppStorageRemove attribute={lang.labels.interval} description={storage.interval}></AppStorageRemove>
              </button>
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.start_date.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("start_date")}>
                <AppStorageRemove attribute={lang.labels.start} description={dateFormat(lang.code, storage.start_date)}></AppStorageRemove>
              </button>
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.end_date.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("end_date")}>
                <AppStorageRemove attribute={lang.labels.end} description={dateFormat(lang.code, storage.end_date)}></AppStorageRemove>
              </button>
              { storage.contact &&
                <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.contact.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("contact")}>
                  <AppStorageRemove attribute={lang.labels.contact} description={storage.contact_label || contactSelected(contactsForCompany ?? [], storage.contact)}></AppStorageRemove>
                </button>
              }
              <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.treasury.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("treasury")}>
                <AppStorageRemove attribute={lang.labels.treasury} description={treasuryAccountSelected(treasuryAccountsForCompany ?? [], storage.treasury)}></AppStorageRemove>
              </button>
              { storage.status &&
                <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.status.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("status")}>
                  <AppStorageRemove attribute={lang.labels.status} description={statusDocumentValue(lang, storage.status)}></AppStorageRemove>
                </button>
              }
              { storage.validity &&
                <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.validity.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("validity")}>
                  <AppStorageRemove attribute={lang.labels.validity} description={validityValue(lang, storage.validity)}></AppStorageRemove>
                </button>
              }
              { storage.payment &&
                <button className={`btn btn-sm btn-info btn-color-dark text-nowrap fs-8 px-2 py-1 mx-1 ${storage.payment.length === 0 && "d-none"}`} type="button" onClick={() => executeFilterRemove("payment")}>
                  <AppStorageRemove attribute={lang.labels.payment} description={paymentAllValue(lang, storage.payment)}></AppStorageRemove>
                </button>
              }
            </div>
          }
        </>
      }
      <AppModalProviderInvoiceExport companyForUser={companyForUser} grouped={{state: button.group, desc: filter.group}} providerInvoices={records as ProviderInvoice[]} providerInvoiceGroups={recordGroups as ProviderInvoiceGroup[]} filter={filter} vatSubject={vatSubject}></AppModalProviderInvoiceExport>
      <AppModalProviderOrderExport companyForUser={companyForUser} grouped={{state: button.group, desc: filter.group}} providerOrders={records as ProviderOrder[]} providerOrderGroups={recordGroups as ProviderOrderGroup[]} filter={filter} vatSubject={vatSubject}></AppModalProviderOrderExport>
      <AppModalExpenseExport companyForUser={companyForUser} grouped={{state: button.group, desc: filter.group}} expenses={records as Expense[]} expenseGroups={recordGroups as ExpenseGroup[]} filter={filter} vatSubject={vatSubject}></AppModalExpenseExport>
    </>
  )
};

export default AppPurchaseListHeader;
